import { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Mic, PhoneOff } from 'lucide-react';
import { DraggablePanel } from './DraggablePanel';
import { useLayoutStore } from '../stores/layoutStore';
import { useVoiceSession } from './VoiceSessionContext';
import { useCanvasConfig } from './CanvasContext';

const BAR_COUNT = 32;
const BAR_COUNT_COLLAPSED = 12;

export function VoiceWidget() {
  const { panels, showPanel, voiceDocked, navSidebarExpanded, pan } = useLayoutStore();
  const { persona } = useCanvasConfig();
  const assistantName = persona.assistantName ?? 'Assistant';
  const live = useVoiceSession();

  // Auto-open the voice panel when a call starts (via button OR parent event).
  useEffect(() => {
    if (live.state === 'connecting' || live.state === 'listening' || live.state === 'speaking') {
      if (!panels.voice?.visible) showPanel('voice');
    }
  }, [live.state, panels.voice?.visible, showPanel]);

  const collapsedDock = voiceDocked && !navSidebarExpanded;
  const barCount = collapsedDock ? BAR_COUNT_COLLAPSED : BAR_COUNT;

  const bars = useMemo(
    () =>
      Array.from({ length: barCount }, (_, i) => ({
        id: i,
        baseHeight: 4 + Math.sin(i * 0.5) * 8,
        phase: (i / barCount) * Math.PI * 2,
      })),
    [barCount],
  );

  const layout = panels.voice;
  if (!layout?.visible) return null;

  const isSpeaking = live.state === 'speaking';
  const isListening = live.state === 'listening';
  const isConnecting = live.state === 'connecting';
  const isCallActive = isListening || isSpeaking || isConnecting;

  const rawLevel = isSpeaking ? live.outputLevel : isListening ? live.inputLevel : 0;
  const level = Math.min(1, Math.pow(rawLevel, 0.6) * 3);

  const isError = live.state === 'error';
  const statusLabel = (() => {
    if (isError) return live.error ?? 'Voice transport error';
    if (isConnecting) return 'Connecting...';
    if (isSpeaking) return `${assistantName} is speaking`;
    if (isListening) return 'Listening';
    return 'Tap Start to begin voice conversation';
  })();

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const waveform = (
    <div
      className={
        collapsedDock
          ? 'flex flex-col items-center justify-center gap-1 flex-1 py-2 px-1 min-h-0'
          : 'flex items-center justify-center gap-[2px] h-14 px-4 py-3'
      }
    >
      {bars.map((bar) => {
        const edgeWeight = 1 - Math.abs(bar.id - barCount / 2) / (barCount / 2);
        const amplitude = level * 28 * (0.35 + 0.65 * edgeWeight);
        const targetHeight = isCallActive ? bar.baseHeight + amplitude : 4;
        const finalHeight = prefersReducedMotion ? bar.baseHeight : targetHeight;
        if (collapsedDock) {
          const targetWidth = isCallActive ? 10 + amplitude * 0.4 : 10;
          return (
            <motion.div
              key={bar.id}
              className="h-[2px] rounded-full max-w-[20px]"
              animate={prefersReducedMotion ? false : { width: targetWidth }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
              style={{
                width: 10,
                background: isError
                  ? 'var(--landi-color-error, #EF4444)'
                  : isSpeaking
                    ? 'var(--landi-color-accent, #C9A227)'
                    : 'var(--landi-color-primary, #0D7377)',
              }}
            />
          );
        }
        return (
          <motion.div
            key={bar.id}
            className="w-[2px] rounded-full"
            animate={prefersReducedMotion ? false : { height: finalHeight }}
            transition={{ duration: 0.12, ease: 'easeOut' }}
            style={{
              height: prefersReducedMotion ? bar.baseHeight : 4,
              background: isError
                ? 'var(--landi-color-error, #EF4444)'
                : isSpeaking
                  ? 'var(--landi-color-accent, #C9A227)'
                  : 'var(--landi-color-primary, #0D7377)',
            }}
          />
        );
      })}
    </div>
  );

  const statusBlock = !collapsedDock ? (
    <div className="px-4 pb-3">
      <p className="text-[10px] text-canvas-faint uppercase tracking-wider mb-1">
        {isError ? 'Error' : isSpeaking ? assistantName : isListening ? 'You' : 'Status'}
      </p>
      {isError ? (
        <div role="alert" aria-live="assertive" className="space-y-2">
          <p className="flex items-center gap-1.5 text-sm text-[#EF4444] font-medium">
            <AlertCircle size={14} aria-hidden="true" />
            <span className="truncate">{statusLabel}</span>
          </p>
          <button
            type="button"
            onClick={() => void live.start()}
            className="text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded"
            style={{
              color: 'var(--landi-color-primary, #0D7377)',
              outlineColor: 'var(--landi-color-primary, #0D7377)',
            }}
          >
            Try again
          </button>
        </div>
      ) : (
        <p className="text-sm italic truncate text-canvas-muted" role="status" aria-live="polite">
          {statusLabel}
          {live.lastTranscript && isSpeaking ? ` — ${live.lastTranscript}` : ''}
        </p>
      )}
    </div>
  ) : null;

  const controls = collapsedDock ? (
    <div className="px-1.5 pb-2 flex flex-col items-center gap-1.5">
      <button
        onClick={() => void live.toggle()}
        disabled={!live.available || isConnecting}
        title={isCallActive ? 'End voice' : 'Start voice'}
        aria-label={isCallActive ? 'End voice' : 'Start voice'}
        className="w-8 h-8 flex items-center justify-center rounded-lg border border-canvas-border text-canvas-primary hover:bg-canvas-surface-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Mic size={14} />
      </button>
      <button
        onClick={() => void live.stop()}
        disabled={!isCallActive}
        title="End call"
        aria-label="End call"
        className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#1A1A1A] text-white hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <PhoneOff size={14} />
      </button>
    </div>
  ) : (
    <div className="px-4 pb-4 flex gap-2">
      <button
        onClick={() => void live.toggle()}
        disabled={!live.available || isConnecting}
        className="flex-1 py-2 rounded-lg border border-canvas-border text-sm font-medium text-canvas-muted hover:bg-canvas-surface-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCallActive ? 'End' : isConnecting ? 'Connecting…' : 'Start'}
      </button>
      <button
        onClick={() => void live.stop()}
        disabled={!isCallActive}
        className="flex-1 py-2 rounded-lg bg-[#1A1A1A] text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        End Call
      </button>
    </div>
  );

  return (
    <DraggablePanel
      id="voice"
      title={collapsedDock ? 'Voice' : 'Voice Conversation'}
      docked={voiceDocked}
      sidebarDocked={voiceDocked}
      positionOffset={voiceDocked ? undefined : pan}
    >
      <div className={`flex flex-col h-full ${collapsedDock ? 'items-center' : ''}`}>
        {waveform}
        {statusBlock}
        {controls}
      </div>
    </DraggablePanel>
  );
}
